/*
 * Copyright (c) Nexign, JSC, 2020-2024.
 */
/**
 * The BreadCrumbs types
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.32.0
 * @since 1.2.0
 */

import { MouseEvent } from 'react';

import { Dispatch } from 'redux';

import {
    getPageAccountInfoUrlRegex,
    isPageAccountInfoUrlExternal,
} from '../../pages/PageAccountInfo/utils/getAccountInfoUrl';
import {
    getPageAutoReportIdUrlRegex,
    isPageAutoReportIdUrlExternal,
} from '../../pages/PageAutoReportId/utils/getPageAutoReportIdUrl';
import { getCampaignsPageUrl } from '../../pages/PageCampaign/utils/getCampaignsPageUrl';
import {
    getPageInquiryInfoUrlRegex,
    isPageInquiryInfoExternalUrl,
} from '../../pages/PageInquiryInfo/utils/getInquiryInfoUrl';
import { getPageOrderIdUrlRegex, isPageOrderIdExternalUrl } from '../../pages/PageOrderId/utils/getPageOrderIdUrl';
import {
    getPagePaymentsAccountIdUrlRegex,
    getPagePaymentsUrlRegex,
    isPagePaymentsExternalUrl,
} from '../../pages/PagePayments/utils/getPaymentsUrl';
import { ExternalRoutePath } from '../../routes/constants/externalRoutes';
import { RoutePath } from '../../routes/constants/routesConstants';
import { pushGTMAnalytic } from '../analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../analytics/GoogleTagManager/GTMOperations';
import { IAction, IActionWithPayload } from '../app/createAction';
import { ECommonTranslationPages, ICommonTranslationPages } from '../internationalization/types/common/pages';
import { guardComponentType } from '../role/reducer/guardReducer';
import { BREADCRUMBS_ADD, BREADCRUMBS_CLEAR, BREADCRUMBS_GO_BACK } from './breadCrumbsConstants';

export interface IBreadCrumb {
    isUrlExternal?: boolean;
    onClick?: (event: MouseEvent) => void;
    title: string;
    url?: string;
}

export type IAddBreadCrumbs = IActionWithPayload<typeof BREADCRUMBS_ADD, IBreadCrumb[]>;

export type IClearBreadCrumbs = IAction<typeof BREADCRUMBS_CLEAR>;

export type TBreadCrumbsGoBack = IAction<typeof BREADCRUMBS_GO_BACK>;

export enum EBreadCrumbType {
    accountCashback = 'cashback',
    accountId = 'accountId',
    accounts = 'accounts',
    autoReport = 'autoReport',
    autoReportId = 'autoReportId',
    bills = 'bills',
    billsAccountId = 'billsAccountId',
    billsUnpaid = 'billsUnpaid',
    billsUnpaidAccountId = 'billsUnpaidAccountId',
    campaigns = 'campaigns',
    dashboard = 'dashboard',
    expensesAccountId = 'expensesAccountId',
    expensesAccountIdSubscribers = 'expensesAccountIdSubscribers',
    expensesAccounts = 'expensesAccounts',
    expensesAccountsSubscribers = 'expensesAccountsSubscribers',
    expensesBillsAccountId = 'expensesBillsAccountId',
    expensesBillsAccountIdSubscribers = 'expensesBillsAccountIdSubscribers',
    expensesBillsAccounts = 'expensesBillsAccounts',
    expensesBillsAccountsSubscribers = 'expensesBillsAccountsSubscribers',
    expensesBillsUnpaidAccountId = 'expensesBillsUnpaidAccountId',
    expensesBillsUnpaidAccountIdSubscribers = 'expensesBillsUnpaidAccountIdSubscribers',
    expensesBillsUnpaidAccounts = 'expensesBillsUnpaidAccounts',
    expensesBillsUnpaidAccountsSubscribers = 'expensesBillsUnpaidAccountsSubscribers',
    expensesSubscriberFixed = 'expensesSubscriberFixed',
    expensesSubscriberMobile = 'expensesSubscriberMobile',
    inquiries = 'inquiries',
    inquiryInfo = 'inquiryInfo',
    news = 'news',
    orderId = 'orderId',
    orders = 'orders',
    payments = 'payments',
    paymentsAccountId = 'paymentsAccountId',
    products = 'products',
    productsFromSubscriberId = 'productsFromSubscriberId',
    productsFromSubscribers = 'productsFromSubscribers',
    reportsConstructor = 'reportsCosntructor',
    reportsConstructorFromAccount = 'reportsConstructorFromAccount',
    reportsConstructorFromAccountList = 'reportsConstructorFromAccountList',
    reportsConstructorFromMobileList = 'reportsConstructorFromMobileList',
    reportsConstructorFromAutoReports = 'reportsConstructorFromAutoReports',
    reportsConstructorSubscribers = 'reportsConstructorSubscribers',
    settingsLabels = 'settingsLabels',
    settingsAutoReports = 'settingsAutoReports',
    subscriberFixed = 'subscriberFixed',
    subscriberFixedInfo = 'subscriberFixedInfo',
    subscriberMobile = 'subscriberMobile',
    subscriberMobileInfo = 'subscriberMobileInfo',

    // wizards
    additionalAccounts = 'additionalAccounts',

    additionalReports = 'additionalReports',
    billsDoc = 'billsDoc',
    callsDetail = 'callsDetail',
    changeLock = 'changeLock',
    expensesDetail = 'expensesDetail',
    importPersonalData = 'importPersonalData',
    labelsImport = 'labelsImport',
    moneyTransfer = 'moneyTransfer',
    productOfferings = 'productOfferings',
    wizardMultiSearch = 'wizardMultiSearch',
    wizardOrderTemplateCreate = 'wizardOrderTemplateCreate',
    wizardOrderTemplateEdit = 'wizardOrderTemplateEdit',
    wizardOrderReport = 'wizardOrderReport',
    wizardCollationReport = 'wizardCollationReport',
    wizardImportSubscribersLimits = 'wizardImportSubscribersLimits',
    wizardChangeRole = 'wizardChangeRole',
}

export const mapBreadCrumbTypeToData = (
    dispatch: Dispatch,
    features: guardComponentType,
    pagesTranslation: ICommonTranslationPages,
): {
    [key in EBreadCrumbType]: {
        isUrlExternal?: boolean;
        onClick?: (event: MouseEvent) => void;
        title: ECommonTranslationPages;
        url?: string;
    };
} => ({
    [EBreadCrumbType.accounts]: {
        title: ECommonTranslationPages.personalAccount,
        url: RoutePath.account,
    },
    [EBreadCrumbType.accountCashback]: {
        onClick: () => {
            dispatch(
                pushGTMAnalytic({
                    operation: GTMOperations.navigation.clickOnBreadCrumbs(
                        pagesTranslation[ECommonTranslationPages.cashback],
                    ),
                    withAccountType: true,
                    withSusrId: true,
                    withUserRole: true,
                }),
            );
        },
        title: ECommonTranslationPages.cashback,
        url: RoutePath.cashback,
    },
    [EBreadCrumbType.accountId]: {
        isUrlExternal: isPageAccountInfoUrlExternal(features),
        title: ECommonTranslationPages.account,
        url: getPageAccountInfoUrlRegex(features),
    },
    [EBreadCrumbType.autoReport]: {
        title: ECommonTranslationPages.autoReport,
        url: RoutePath.settingsAutoReports,
    },
    [EBreadCrumbType.autoReportId]: {
        isUrlExternal: isPageAutoReportIdUrlExternal(features),
        title: ECommonTranslationPages.autoReportId,
        url: getPageAutoReportIdUrlRegex(features),
    },
    [EBreadCrumbType.bills]: {
        title: ECommonTranslationPages.bills,
        url: RoutePath.bills,
    },
    [EBreadCrumbType.billsUnpaid]: {
        title: ECommonTranslationPages.bills,
        url: RoutePath.billsUnpaid,
    },
    [EBreadCrumbType.billsAccountId]: {
        title: ECommonTranslationPages.bills,
        url: RoutePath.billsAccount,
    },
    [EBreadCrumbType.billsUnpaidAccountId]: {
        title: ECommonTranslationPages.bills,
        url: RoutePath.billsUnpaidAccount,
    },
    [EBreadCrumbType.campaigns]: {
        title: ECommonTranslationPages.campaigns,
        url: getCampaignsPageUrl(),
    },
    [EBreadCrumbType.expensesBillsAccounts]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerBillsAccounts,
    },
    [EBreadCrumbType.expensesBillsAccountsSubscribers]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerBillsAccountsSubscribers,
    },
    [EBreadCrumbType.expensesBillsUnpaidAccounts]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerBillsUnpaidAccounts,
    },
    [EBreadCrumbType.expensesBillsUnpaidAccountsSubscribers]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerBillsUnpaidAccountsSubscribers,
    },
    [EBreadCrumbType.expensesBillsAccountId]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerBillsAccountId,
    },
    [EBreadCrumbType.expensesBillsAccountIdSubscribers]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerBillsAccountIdSubscribers,
    },
    [EBreadCrumbType.expensesBillsUnpaidAccountId]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerBillsUnpaidAccountId,
    },
    [EBreadCrumbType.expensesBillsUnpaidAccountIdSubscribers]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerBillsUnpaidAccountIdSubscribers,
    },
    [EBreadCrumbType.dashboard]: {
        title: ECommonTranslationPages.dashboard,
        url: RoutePath.dashboard,
    },
    [EBreadCrumbType.expensesAccounts]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerAccount,
    },
    [EBreadCrumbType.expensesAccountsSubscribers]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerAccountSubscribers,
    },
    [EBreadCrumbType.expensesAccountId]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerAccountId,
    },
    [EBreadCrumbType.expensesAccountIdSubscribers]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerAccountIdSubscribers,
    },
    [EBreadCrumbType.inquiries]: {
        title: ECommonTranslationPages.inquiry,
        url: RoutePath.inquiries,
    },
    [EBreadCrumbType.inquiryInfo]: {
        isUrlExternal: isPageInquiryInfoExternalUrl(features),
        title: ECommonTranslationPages.inquiryInfo,
        url: getPageInquiryInfoUrlRegex(features),
    },

    // News
    [EBreadCrumbType.news]: {
        title: ECommonTranslationPages.news,
        url: RoutePath.news,
    },

    // Orders

    [EBreadCrumbType.orderId]: {
        isUrlExternal: isPageOrderIdExternalUrl(features),
        title: ECommonTranslationPages.pageOrderId,
        url: getPageOrderIdUrlRegex(features),
    },
    [EBreadCrumbType.orders]: {
        title: ECommonTranslationPages.historyOrder,
        url: RoutePath.orders,
    },

    // Payments
    [EBreadCrumbType.payments]: {
        isUrlExternal: isPagePaymentsExternalUrl(features),
        title: ECommonTranslationPages.pagePayments,
        url: getPagePaymentsUrlRegex(features),
    },
    [EBreadCrumbType.paymentsAccountId]: {
        isUrlExternal: isPagePaymentsExternalUrl(features),
        title: ECommonTranslationPages.pagePayments,
        url: getPagePaymentsAccountIdUrlRegex(features),
    },

    // Products
    [EBreadCrumbType.products]: {
        title: ECommonTranslationPages.products,
        url: RoutePath.products,
    },
    [EBreadCrumbType.productsFromSubscribers]: {
        title: ECommonTranslationPages.products,
        url: RoutePath.productsFromSubscribers,
    },
    [EBreadCrumbType.productsFromSubscriberId]: {
        title: ECommonTranslationPages.products,
        url: RoutePath.productsFromSubscriberId,
    },

    // reports constructor
    [EBreadCrumbType.reportsConstructor]: {
        title: ECommonTranslationPages.reportsConstructor,
        url: RoutePath.reportsConstructor,
    },
    [EBreadCrumbType.reportsConstructorFromAccount]: {
        title: ECommonTranslationPages.reportsConstructor,
        url: RoutePath.reportsConstructorFromAccount,
    },
    [EBreadCrumbType.reportsConstructorFromAccountList]: {
        title: ECommonTranslationPages.reportsConstructor,
        url: RoutePath.reportsConstructorFromAccountList,
    },
    [EBreadCrumbType.reportsConstructorFromMobileList]: {
        title: ECommonTranslationPages.reportsConstructor,
        url: RoutePath.reportsConstructorFromMobileList,
    },
    [EBreadCrumbType.reportsConstructorFromAutoReports]: {
        title: ECommonTranslationPages.reportsConstructor,
        url: RoutePath.reportsConstructorFromAutoReports,
    },
    [EBreadCrumbType.reportsConstructorSubscribers]: {
        title: ECommonTranslationPages.reportsConstructorSubscribers,
        url: RoutePath.reportsConstructorSubscriber,
    },

    [EBreadCrumbType.settingsLabels]: {
        title: ECommonTranslationPages.settings,
        url: RoutePath.settingsLabels,
    },
    [EBreadCrumbType.settingsAutoReports]: {
        title: ECommonTranslationPages.settings,
        url: RoutePath.settingsAutoReports,
    },
    [EBreadCrumbType.subscriberFixed]: {
        title: ECommonTranslationPages.subscriberFixed,
        url: RoutePath.subscriberFixed,
    },
    [EBreadCrumbType.subscriberFixedInfo]: {
        title: ECommonTranslationPages.subscriberId,
        url: ExternalRoutePath.fixedInfo,
        isUrlExternal: true,
    },
    [EBreadCrumbType.subscriberMobile]: {
        title: ECommonTranslationPages.subscriberMobile,
        url: RoutePath.subscriberMobile,
    },
    [EBreadCrumbType.subscriberMobileInfo]: {
        title: ECommonTranslationPages.subscriberId,
        url: RoutePath.subscriberId,
    },
    [EBreadCrumbType.expensesSubscriberFixed]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerSubscriberFixed,
    },
    [EBreadCrumbType.expensesSubscriberMobile]: {
        title: ECommonTranslationPages.expensesAnalyzer,
        url: RoutePath.expensesAnalyzerSubscriberMobile,
    },

    /**
     * WIZARDS
     */

    [EBreadCrumbType.additionalAccounts]: {
        title: ECommonTranslationPages.wizardAdditionalAccounts,
    },
    [EBreadCrumbType.additionalReports]: {
        title: ECommonTranslationPages.wizardAdditionalReports,
    },
    [EBreadCrumbType.billsDoc]: {
        title: ECommonTranslationPages.wizardBillsDoc,
    },
    [EBreadCrumbType.callsDetail]: {
        title: ECommonTranslationPages.callsDetail,
    },
    [EBreadCrumbType.changeLock]: {
        title: ECommonTranslationPages.wizardChangeLock,
    },
    [EBreadCrumbType.expensesDetail]: {
        title: ECommonTranslationPages.wizardExpensesDetail,
    },
    [EBreadCrumbType.importPersonalData]: {
        title: ECommonTranslationPages.wizardImportPersonalData,
    },
    [EBreadCrumbType.labelsImport]: {
        title: ECommonTranslationPages.labelsImport,
    },
    [EBreadCrumbType.moneyTransfer]: {
        title: ECommonTranslationPages.moneyTransfer,
    },
    [EBreadCrumbType.wizardMultiSearch]: {
        title: ECommonTranslationPages.wizardMultiSearch,
    },
    [EBreadCrumbType.productOfferings]: {
        title: ECommonTranslationPages.productOfferings,
    },
    [EBreadCrumbType.wizardOrderTemplateCreate]: {
        title: ECommonTranslationPages.wizardOrderTemplateCreate,
    },
    [EBreadCrumbType.wizardOrderTemplateEdit]: {
        title: ECommonTranslationPages.wizardOrderTemplateEdit,
    },
    [EBreadCrumbType.wizardOrderReport]: {
        title: ECommonTranslationPages.wizardOrderReport,
    },
    [EBreadCrumbType.wizardCollationReport]: {
        title: ECommonTranslationPages.wizardCollationReport,
    },
    [EBreadCrumbType.wizardImportSubscribersLimits]: {
        title: ECommonTranslationPages.wizardImportSubscribersLimits,
    },
    [EBreadCrumbType.wizardChangeRole]: {
        title: ECommonTranslationPages.wizardChangeRole,
    },
});
